import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { FiCheck, FiCalendar, FiMapPin, FiUsers } from 'react-icons/fi';
import api from '../services/api';

const BookingConfirmation = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyAndFetch = async () => {
      try {
        // Verify payment if session_id exists
        if (sessionId) {
          await api.verifyPaymentSession(sessionId);
        }

        // Fetch booking details
        const data = await api.getBookingById(bookingId);
        setBooking(data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    verifyAndFetch();
  }, [bookingId, sessionId]);

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  if (!booking) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px' }}>
        <h2>Booking not found</h2>
        <Link to="/apartments" style={{ color: '#1a3a5c' }}>Browse apartments</Link>
      </div>
    );
  }

  const styles = {
    container: { maxWidth: '700px', margin: '0 auto', padding: '60px 24px' },
    successIcon: {
      width: '80px',
      height: '80px',
      background: '#E8F5E9',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 24px',
      color: '#4CAF50',
      fontSize: '2.5rem'
    },
    title: { fontSize: '2rem', fontWeight: '700', textAlign: 'center', marginBottom: '8px' },
    subtitle: { textAlign: 'center', color: '#555', marginBottom: '40px' },
    card: { background: 'white', border: '1px solid #eee', borderRadius: '16px', overflow: 'hidden', marginBottom: '24px' },
    cardHeader: { background: '#f7f7f7', padding: '20px 24px', borderBottom: '1px solid #eee' },
    cardBody: { padding: '24px' },
    reference: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    refCode: { fontSize: '1.25rem', fontWeight: '700', color: '#1a3a5c' },
    apartmentInfo: { display: 'flex', gap: '16px', marginBottom: '24px' },
    apartmentImage: { width: '100px', height: '75px', borderRadius: '8px', objectFit: 'cover' },
    detail: { display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' },
    detailIcon: { color: '#1a3a5c', marginTop: '2px' },
    detailLabel: { color: '#555', fontSize: '0.875rem' },
    detailValue: { fontWeight: '500' },
    divider: { height: '1px', background: '#eee', margin: '24px 0' },
    priceRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '8px' },
    priceTotal: { display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1.125rem', paddingTop: '16px', borderTop: '1px solid #eee', marginTop: '16px' },
    actions: { display: 'flex', gap: '16px', justifyContent: 'center' },
    btn: { padding: '12px 24px', borderRadius: '8px', fontWeight: '500', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' },
    btnPrimary: { background: '#1a3a5c', color: 'white', border: 'none' },
    btnOutline: { background: 'white', color: '#222', border: '1px solid #ddd' }
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
  });

  return (
    <div style={styles.container}>
      <div style={styles.successIcon}>
        <FiCheck />
      </div>

      <h1 style={styles.title}>Booking Confirmed!</h1>
      <p style={styles.subtitle}>
        Thank you for your booking. A confirmation email has been sent to {booking.guestInfo?.email}
      </p>

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.reference}>
            <span>Booking Reference</span>
            <span style={styles.refCode}>{booking.bookingReference}</span>
          </div>
        </div>

        <div style={styles.cardBody}>
          <div style={styles.apartmentInfo}>
            <img
              src={booking.apartment?.images?.[0] || '/images/placeholder-apartment.jpg'}
              alt={booking.apartment?.name}
              style={styles.apartmentImage}
            />
            <div>
              <h3 style={{ fontWeight: '600', marginBottom: '4px' }}>{booking.apartment?.name}</h3>
              <p style={{ color: '#555', fontSize: '0.875rem' }}>
                <FiMapPin style={{ marginRight: '4px' }} />
                {booking.apartment?.address?.city}
              </p>
            </div>
          </div>

          <div style={styles.detail}>
            <FiCalendar style={styles.detailIcon} />
            <div>
              <div style={styles.detailLabel}>Check-in</div>
              <div style={styles.detailValue}>{formatDate(booking.checkIn)} from {booking.apartment?.checkInTime}</div>
            </div>
          </div>

          <div style={styles.detail}>
            <FiCalendar style={styles.detailIcon} />
            <div>
              <div style={styles.detailLabel}>Check-out</div>
              <div style={styles.detailValue}>{formatDate(booking.checkOut)} by {booking.apartment?.checkOutTime}</div>
            </div>
          </div>

          <div style={styles.detail}>
            <FiUsers style={styles.detailIcon} />
            <div>
              <div style={styles.detailLabel}>Guests</div>
              <div style={styles.detailValue}>
                {booking.guests?.adults} adult{booking.guests?.adults !== 1 ? 's' : ''}
                {booking.guests?.children > 0 && `, ${booking.guests?.children} child${booking.guests?.children !== 1 ? 'ren' : ''}`}
              </div>
            </div>
          </div>

          <div style={styles.divider} />

          <h4 style={{ fontWeight: '600', marginBottom: '16px' }}>Payment Summary</h4>
          <div style={styles.priceRow}>
            <span>{booking.nights} night{booking.nights !== 1 ? 's' : ''}</span>
            <span>£{booking.pricing?.basePrice?.toFixed(2)}</span>
          </div>
          {booking.pricing?.discount > 0 && (
            <div style={{ ...styles.priceRow, color: '#008A05' }}>
              <span>Discount</span>
              <span>-£{booking.pricing?.discount?.toFixed(2)}</span>
            </div>
          )}
          {booking.pricing?.cleaningFee > 0 && (
            <div style={styles.priceRow}>
              <span>Cleaning fee</span>
              <span>£{booking.pricing?.cleaningFee?.toFixed(2)}</span>
            </div>
          )}
          <div style={styles.priceRow}>
            <span>Service fee</span>
            <span>£{booking.pricing?.serviceFee?.toFixed(2)}</span>
          </div>
          <div style={styles.priceTotal}>
            <span>Total paid</span>
            <span>£{booking.pricing?.totalPrice?.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div style={styles.actions}>
        <Link to="/my-bookings" style={{ ...styles.btn, ...styles.btnPrimary }}>
          View My Bookings
        </Link>
        <Link to="/apartments" style={{ ...styles.btn, ...styles.btnOutline }}>
          Browse More
        </Link>
      </div>
    </div>
  );
};

export default BookingConfirmation;
